import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleRatingBadge from 'src/components/simple-rating-badge';

import Link from '@/components/link';
import { getFriendlyId } from '@/utils/friendlyId';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTablePropsWithoutUrlHash } from '@/utils/table/hooks/useGetTablePropsWithoutUrlHash';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import type {
  OversightPerformanceFlatFields,
  OversightPerformanceRegisterFields,
} from './types';
import { useOversightPerformanceLabelledFields } from './useOversightPerformanceLabelledFields';

const useGetFieldConfig = (
  onEdit: (item: OversightPerformanceFlatFields) => void
): TableFields<OversightPerformanceRegisterFields> => {
  const { t } = useTranslation(['common']);

  const { getByValue } = useRating('effectiveness', 'oversight');

  return useMemo<TableFields<OversightPerformanceRegisterFields>>(
    () => ({
      FriendlyID: {
        header: t('columns.id'),
        cell: (item) => (
          <Link variant={'secondary'} href={'#'} onFollow={() => onEdit(item)}>
            {getFriendlyId(Parent_Type_Enum.TestResult, item.SequentialId)}
          </Link>
        ),
      },
      Title: {
        formId: 'control_test_second_line_result',
        fieldId: 'Title',
        cell: (item) => item.Title,
        sortingField: 'Title',
      },
      OversightAssessmentTitle: {
        formId: 'compliance_monitoring_assessment',
        fieldId: 'Title',
        includeFromTypePostfix: true,
        cell: (item) =>
          item.parents.find((p) => p.oversightAssessment)
            ?.oversightAssessment ? (
            <Link
              isRelativeUrl={true}
              variant={'secondary'}
              href={
                item.parents.find((p) => p.oversightAssessment)
                  ?.oversightAssessment?.Id
              }
            >
              {
                item.parents.find((p) => p.oversightAssessment)
                  ?.oversightAssessment?.Title
              }
            </Link>
          ) : (
            '-'
          ),
        isRowHeader: true,
      },
      TestDate: dateColumnFromConfig({
        header: {
          formId: 'control_test_second_line_result',
          fieldId: 'TestDate',
        },
        dateField: 'TestDate',
      }),
      TestTypeLabelled: {
        formId: 'control_test_second_line_result',
        fieldId: 'TestType',
        cell: (item: OversightPerformanceRegisterFields) => {
          const testTypeLookup = t('testTypes');
          if (item.TestType) {
            const testTypeLabel =
              testTypeLookup[item.TestType as keyof typeof testTypeLookup];
            if (testTypeLabel) {
              return testTypeLabel;
            }

            return '-';
          }
        },
      },
      OverallEffectivenessLabelled: {
        formId: 'control_test_second_line_result',
        fieldId: 'OverallEffectiveness',
        cell: (item) => {
          return (
            <SimpleRatingBadge rating={getByValue(item.OverallEffectiveness)} />
          );
        },
      },
      SubmitterName: {
        formId: 'control_test_second_line_result',
        fieldId: 'Submitter',
        cell: (item) => item.SubmitterName || '-',
        sortingField: 'SubmitterName',
      },
    }),
    [getByValue, onEdit, t]
  );
};

const useGetPerformanceTableProps = (
  onEdit: (item: OversightPerformanceFlatFields) => void,
  records: OversightPerformanceFlatFields[] | undefined
): UseGetTablePropsOptions<OversightPerformanceRegisterFields> => {
  const fields = useGetFieldConfig(onEdit);
  const labelledFields = useOversightPerformanceLabelledFields(records);

  return {
    customAttributeFormIds: ['control_test_oversight_result'],
    data: labelledFields,
    enableFiltering: true,
    entityLabel: 'rating',
    defaultSortingState: {
      sortingColumn: 'TestDate',
      sortingDirection: 'desc',
    },
    initialColumns: [
      'FriendlyID',
      'Title',
      'TestDate',
      'TestTypeLabelled',
      'OverallEffectivenessLabelled',
      'SubmitterName',
    ],
    preferencesStorageKey:
      'ControlOversightPerformanceRegisterTable-Preferences',
    fields,
  };
};

export const useGetCollectionTableProps = (
  onEdit: (item: OversightPerformanceFlatFields) => void,
  records: OversightPerformanceFlatFields[] | undefined
): TablePropsWithActions<OversightPerformanceRegisterFields> => {
  const props = useGetPerformanceTableProps(onEdit, records);

  return useGetTablePropsWithoutUrlHash(props);
};
